import { supabase } from './supabase';
import type { Message, Conversation } from '@/types/database';

export async function getConversations() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data: conversations, error } = await supabase
      .from('conversations')
      .select(`
        *,
        participants:conversation_participants(
          user:profiles(
            id,
            full_name,
            avatar_url
          )
        )
      `)
      .order('last_message_at', { ascending: false });

    if (error) throw error;

    // Get unread counts
    const { data: unreadCounts, error: unreadError } = await supabase
      .rpc('get_unread_message_count', { 
        user_uuid: user.id 
      });

    if (unreadError) throw unreadError;

    return (conversations || []).map(conversation => ({
      ...conversation,
      unread_count: unreadCounts?.find(c => c.conversation_id === conversation.id)?.unread_count || 0
    }));
  } catch (error) {
    console.error('Error fetching conversations:', error);
    return [];
  }
}

export async function getConversationMessages(conversationId: string) {
  try {
    const { data: messages, error } = await supabase
      .from('messages')
      .select(`
        *,
        sender:profiles(
          id,
          full_name,
          avatar_url
        )
      `)
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return messages || [];
  } catch (error) {
    console.error('Error fetching messages:', error);
    return [];
  }
}

export async function sendMessage(
  conversationId: string, 
  content: string,
  mediaUrl?: string,
  mediaType?: 'image' | 'video'
) {
  try {
    const { data: message, error } = await supabase
      .rpc('safely_send_message', {
        conversation_uuid: conversationId,
        content: content.trim(),
        media_url: mediaUrl,
        media_type: mediaType
      });

    if (error) throw error;

    // Get the full message with sender info
    const { data: fullMessage, error: fetchError } = await supabase
      .from('messages')
      .select(`
        *,
        sender:profiles(
          id,
          full_name,
          avatar_url
        )
      `)
      .eq('id', message.id)
      .single();

    if (fetchError) throw fetchError;
    return fullMessage;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
}

export async function createConversation(participantIds: string[]) {
  try {
    // Create the conversation
    const { data: conversation, error: conversationError } = await supabase
      .from('conversations')
      .insert({})
      .select()
      .single();

    if (conversationError) throw conversationError;

    // Add participants using the secure function
    const { error: participantsError } = await supabase
      .rpc('safely_add_participants', {
        conversation_uuid: conversation.id,
        participant_uuids: participantIds
      });

    if (participantsError) throw participantsError;

    // Get the full conversation with participants
    const { data: fullConversation, error: fetchError } = await supabase
      .from('conversations')
      .select(`
        *,
        participants:conversation_participants(
          user:profiles(
            id,
            full_name,
            avatar_url
          )
        )
      `)
      .eq('id', conversation.id)
      .single();

    if (fetchError) throw fetchError;
    return fullConversation;
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
}

export async function markConversationAsRead(conversationId: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .rpc('mark_messages_as_read', {
        conversation_uuid: conversationId,
        user_uuid: user.id
      });

    if (error) throw error;
  } catch (error) {
    console.error('Error marking conversation as read:', error);
  }
}

export async function deleteMessage(messageId: string) {
  try {
    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId)
      .eq('sender_id', (await supabase.auth.getUser()).data.user?.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
}

export async function deleteConversation(conversationId: string) {
  try {
    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', conversationId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
}

export function subscribeToMessages(
  conversationId: string,
  callback: (message: Message) => void
) {
  return supabase
    .channel(`messages:${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      (payload) => {
        callback(payload.new as Message);
      }
    )
    .subscribe();
}

export function subscribeToConversations(
  callback: (conversation: Conversation) => void
) {
  return supabase
    .channel('conversations')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'conversations'
      },
      (payload) => {
        callback(payload.new as Conversation);
      }
    )
    .subscribe();
}